import { useState } from "react";
import { Moai } from "./Moai.js";
import { FILTRES_SOIREES, filtrerSoirees, filtresUtiles, membresDe, signalable, type FiltreSoirees, type SoireePassee } from "./historique.js";
import type { Article, Profil } from "./profil.js";
import { SKINS, type SkinId } from "./skins.js";
import { RAYON_HOA_EN_PAUSE } from "./ecrans2.js";
import { Barre, Ecran, Feuille, FeuilleReglable, Fleur, Parole, Retour, pluriel } from "./ui.js";

/**
 * Mon compte, suite : « Personnaliser » (thème, Hoa, bientôt la musique), rangé
 * comme la boutique ; « Tes soirées », une feuille qu'on règle par sa
 * poignée ; « Signaler », qui vise une salle passée et ses membres.
 */

function capitale(t: string): string {
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function dateCourte(ms: number): string {
  return capitale(new Date(ms).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" }));
}

/* ------------------------------------------------------------------ */
/* Personnaliser                                                       */
/* ------------------------------------------------------------------ */

/*
 * Personnaliser : le thème, la tenue de Hoa et la musique. Le pseudo et la
 * photo vivent dans « Mon compte ». Tant que les accessoires de Hoa sont à
 * redessiner, son rayon montre ce qu'il porte ; la musique est à venir.
 */
type RayonPerso = "theme" | "hoa" | "musique";
const RAYONS_PERSO: Array<[RayonPerso, string]> = [
  ["theme", "Thème"],
  ["hoa", "Hoa"],
  ["musique", "Musique"],
];

export function Personnaliser(props: {
  skin: SkinId;
  profil: Profil | null;
  envoi: boolean;
  erreur: string | null;
  onSkin: (s: SkinId) => void;
  onPorter: (a: Article) => void;
  onBoutique: () => void;
  onCompte: () => void;
  onRetour: () => void;
}): JSX.Element {
  const [rayon, setRayon] = useState<RayonPerso>("theme");
  const [theme, setTheme] = useState<SkinId>(props.skin);
  const p = props.profil;
  const articles = (p?.articles ?? []).filter((a) => a.rayon === "hoa" && (p?.possessions.includes(a.id) || a.prix === 0));
  const [choixHoa, setChoixHoa] = useState<string | null>(null);
  const hoaChoisi = articles.find((a) => a.id === (choixHoa ?? p?.porte.hoa)) ?? articles[0] ?? null;
  const bouton = (() => {
    switch (rayon) {
      case "theme":
        return theme === props.skin
          ? { texte: "Utilisé", actif: false, action: () => undefined }
          : { texte: "Utiliser ce thème", actif: true, action: () => props.onSkin(theme) };
      case "musique":
        return { texte: "Bientôt disponible", actif: false, action: () => undefined };
      case "hoa":
        // Ses accessoires sont à redessiner : il porte son illustration, rien d'autre pour l'instant.
        if (RAYON_HOA_EN_PAUSE) return { texte: "Porté", actif: false, action: () => undefined };
        if (!p) return { texte: "Créer un compte", actif: true, action: props.onCompte };
        if (!hoaChoisi || p.porte.hoa === hoaChoisi.id) return { texte: "Porté", actif: false, action: () => undefined };
        return { texte: "Le porter", actif: true, action: () => props.onPorter(hoaChoisi) };
    }
  })();

  return (
    // Le thème choisi se voit tout de suite, sur cet écran, avant de l'utiliser.
    <Ecran skin={theme} label="Personnaliser">
        <Barre>
          <button type="button" className="hoa-rond" aria-label="Retour" onClick={props.onRetour}>
            <Retour />
          </button>
          <span className="titre">Personnaliser</span>
        </Barre>
        {/* Des onglets dès qu'il y a plus d'un rayon. */}
        {RAYONS_PERSO.length > 1 ? (
          <div className="hoa-rayons" role="tablist" aria-label="Rayons">
            {RAYONS_PERSO.map(([k, l]) => (
              <button key={k} type="button" role="tab" aria-selected={rayon === k} className={rayon === k ? "on" : ""} onClick={() => setRayon(k)}>
                {l}
              </button>
            ))}
          </div>
        ) : null}

        {/* Sous les onglets : ils ne bougent pas d'un rayon à l'autre. */}
        {rayon === "hoa" && RAYON_HOA_EN_PAUSE ? <Parole expression="clin">Je change de tenue : mes accessoires reviennent bientôt.</Parole> : null}
        {rayon === "hoa" && !RAYON_HOA_EN_PAUSE && !p ? <Parole>Mes accessoires se gardent avec un compte.</Parole> : null}
        {rayon === "musique" ? <Parole expression="sourire">Mes musiques arrivent bientôt.</Parole> : null}

        {rayon === "theme" ? (
          <div className="hoa-articles">
            {SKINS.map((s) => (
              <button key={s.id} type="button" className={theme === s.id ? "hoa-article on" : "hoa-article"} aria-pressed={theme === s.id} onClick={() => setTheme(s.id)}>
                <span className="mini" style={{ background: s.apercu[0], border: `2px solid ${s.apercu[2]}` }}>
                  <em style={{ background: s.apercu[3] }} />
                  <i style={{ background: s.apercu[1], width: "80%" }} />
                  <i style={{ background: s.apercu[2], width: "60%" }} />
                  <i style={{ background: s.apercu[2], width: "70%" }} />
                </span>
                <b>{s.nom}</b>
                <span>{props.skin === s.id ? "Utilisé" : "Offert"}</span>
              </button>
            ))}
          </div>
        ) : null}

        {rayon === "hoa" && RAYON_HOA_EN_PAUSE ? (
          <div className="hoa-articles">
            <div className="hoa-article on">
              <span className="vis">
                <Moai expression="sourire" />
              </span>
              <b>Hoa</b>
              <span>Porté</span>
            </div>
          </div>
        ) : null}

        {rayon === "hoa" && !RAYON_HOA_EN_PAUSE ? (
          <div className="hoa-articles">
            {articles.map((a) => (
              <button key={a.id} type="button" className={hoaChoisi?.id === a.id ? "hoa-article on" : "hoa-article"} aria-pressed={hoaChoisi?.id === a.id} onClick={() => setChoixHoa(a.id)}>
                <span className="vis">
                  <Moai accessoire={a.id === "hoa-classique" ? null : a.id} toujours />
                </span>
                <b>{a.nom}</b>
                <span>{p?.porte.hoa === a.id ? "Porté" : "À toi"}</span>
              </button>
            ))}
            <button type="button" className="hoa-article plus" onClick={props.onBoutique}>
              <span className="vis">
                <Fleur taille={34} />
              </span>
              <b>Plus dans la boutique</b>
              <span>{p ? `${p.points} ${pluriel(p.points, "point", "points")}` : "Avec un compte"}</span>
            </button>
          </div>
        ) : null}

        {props.erreur ? (
          <div className="hoa-erreur" role="alert">
            {props.erreur}
          </div>
        ) : null}
        <button type="button" className="hoa-go" style={{ marginTop: "auto" }} disabled={!bouton.actif || props.envoi} onClick={bouton.action}>
          {props.envoi ? "Un instant…" : bouton.texte}
        </button>
    </Ecran>
  );
}

/* ------------------------------------------------------------------ */
/* Tes soirées                                                         */
/* ------------------------------------------------------------------ */

/** Le filtre choisi reste le temps de la visite : on revient d'une soirée sur la même liste. */
let filtreGarde: FiltreSoirees = "toutes";

export function FeuilleSoirees(props: { soirees: SoireePassee[]; onSoiree: (s: SoireePassee) => void; onFermer: () => void }): JSX.Element {
  const utiles = filtresUtiles(props.soirees);
  const [filtre, setFiltre] = useState<FiltreSoirees>(utiles ? filtreGarde : "toutes");
  const liste = filtrerSoirees(props.soirees, utiles ? filtre : "toutes");
  return (
    <FeuilleReglable
      label="Tes soirées"
      onFermer={props.onFermer}
      entete={
        <>
          <div className="hoa-feuille-titre">
            <b>Tes soirées</b>
            <span>{props.soirees.length}</span>
          </div>
          {utiles ? (
            <div className="hoa-rayons" role="group" aria-label="Filtrer">
              {FILTRES_SOIREES.map(([k, l]) => (
                <button
                  key={k}
                  type="button"
                  aria-pressed={filtre === k}
                  className={filtre === k ? "on" : ""}
                  onClick={() => {
                    filtreGarde = k;
                    setFiltre(k);
                  }}
                >
                  {l}
                </button>
              ))}
            </div>
          ) : null}
        </>
      }
    >
      {liste.length === 0 ? <p className="hoa-note">Pas encore de soirée.</p> : null}
      <div className="hoa-liste">
        {liste.map((s) => (
          <button key={s.id} type="button" className="hoa-ligne" onClick={() => props.onSoiree(s)}>
            <div>
              <b>{s.titre}</b>
              <span>
                {dateCourte(s.rdvAt)} · {s.lieu} · {s.personnes} {pluriel(s.personnes, "personne", "personnes")}
              </span>
            </div>
            <em>{s.gain ? `+${s.gain}` : s.reunis ? "réunis" : "—"}</em>
          </button>
        ))}
      </div>
    </FeuilleReglable>
  );
}

/* ------------------------------------------------------------------ */
/* Signaler quelqu'un d'une soirée passée                              */
/* ------------------------------------------------------------------ */

const MOTIFS: Array<[string, string]> = [
  ["propos", "Propos déplacés"],
  ["insistance", "Insistance"],
  ["comportement", "Comportement"],
  ["photo", "Photo de profil"],
  ["illicite", "Contenu illicite"],
  ["autre", "Autre"],
];

export function FeuilleSignalerSalle(props: {
  soirees: SoireePassee[];
  maintenant: number;
  /** Ouverte depuis une soirée : on commence par ses membres. */
  depart: SoireePassee | null;
  envoi: boolean;
  erreur: string | null;
  fait: boolean;
  onEnvoyer: (e: { sessionId: string; moi: string; cibles: string[]; motif: string; texte: string }) => void;
  onFermer: () => void;
}): JSX.Element {
  const possibles = props.soirees.filter((s) => signalable(s, props.maintenant)).sort((a, b) => b.rdvAt - a.rdvAt);
  const [salle, setSalle] = useState<SoireePassee | null>(props.depart && signalable(props.depart, props.maintenant) ? props.depart : null);
  const [cibles, setCibles] = useState<string[]>([]);
  const [motif, setMotif] = useState<string | null>(null);
  const [texte, setTexte] = useState("");

  if (props.fait) {
    return (
      <Feuille label="Signaler" onFermer={props.onFermer}>
        <Parole expression="pouce">C'est transmis à la régie. Personne ne saura que c'est toi.</Parole>
        <button type="button" className="hoa-go" onClick={props.onFermer}>
          Fermer
        </button>
      </Feuille>
    );
  }

  if (!salle) {
    return (
      <Feuille label="Signaler : choisir la soirée" onFermer={props.onFermer}>
        {props.depart && !signalable(props.depart, props.maintenant) ? (
          <Parole expression="question">Cette soirée a plus de sept jours : je ne sais plus qui y était.</Parole>
        ) : (
          <Parole expression="question">{possibles.length ? "C'était quelle soirée ?" : "Aucune soirée de moins de sept jours sur ce téléphone."}</Parole>
        )}
        <div className="hoa-liste">
          {possibles.map((s) => (
            <button key={s.id} type="button" className="hoa-ligne" onClick={() => setSalle(s)}>
              <div>
                <b>{s.titre}</b>
                <span>
                  {dateCourte(s.rdvAt)} · {s.lieu}
                </span>
              </div>
            </button>
          ))}
        </div>
        <button type="button" className="hoa-lien" onClick={props.onFermer}>
          Fermer
        </button>
      </Feuille>
    );
  }

  const membres = membresDe(salle);
  return (
    <Feuille label="Signaler : qui et quoi" onFermer={props.onFermer}>
      <span className="hoa-etiquette">
        {salle.titre} · {dateCourte(salle.rdvAt)}
      </span>
      <span className="hoa-etiquette">Qui</span>
      <div className="hoa-choix" role="group" aria-label="Qui">
        {membres.map((m) => (
          <button
            key={m.pid}
            type="button"
            aria-pressed={cibles.includes(m.pid)}
            onClick={() => setCibles((c) => (c.includes(m.pid) ? c.filter((x) => x !== m.pid) : [...c, m.pid]))}
          >
            {m.pseudo}
          </button>
        ))}
      </div>
      <span className="hoa-etiquette">Quoi</span>
      <div className="hoa-choix" role="group" aria-label="Quoi">
        {MOTIFS.map(([id, nom]) => (
          <button key={id} type="button" aria-pressed={motif === id} onClick={() => setMotif(id)}>
            {nom}
          </button>
        ))}
      </div>
      <textarea
        className="hoa-champ hoa-champ-bloc"
        rows={3}
        maxLength={600}
        aria-label="Ce qui s'est passé (facultatif)"
        placeholder="Ce qui s'est passé (facultatif)"
        value={texte}
        onChange={(e) => setTexte(e.target.value)}
      />
      {props.erreur ? (
        <div className="hoa-erreur" role="alert">
          {props.erreur}
        </div>
      ) : null}
      <button
        type="button"
        className="hoa-go"
        disabled={cibles.length === 0 || !motif || props.envoi}
        onClick={() => motif && props.onEnvoyer({ sessionId: salle.id, moi: salle.moi ?? "", cibles, motif, texte: texte.trim() })}
      >
        {props.envoi ? "Un instant…" : "Envoyer à la régie"}
      </button>
      {props.depart ? null : (
        <button type="button" className="hoa-lien" onClick={() => setSalle(null)}>
          Une autre soirée
        </button>
      )}
    </Feuille>
  );
}
